import $ from "jquery";
import moment from "moment";
import PropTypes from "prop-types";
import React from "react";

/**
 * An input group with a datetimepicker-enabled input, a hidden input with the date value
 * in a format we can use on the serverside, and the necessary magic in between.
 *
 * @example
 *   <DatePicker
 *     id="used-by-label-for"
 *     name="name-for-form-submission"
 *     value="2017-01-31" />
 */
class DatePicker extends React.Component {
  /**
   * Creates the component with the initial state based on props.
   *
   * @param {object} props Initial props
   */
  constructor(props) {
    super(props);
    this.state = { value: this.defaultValue() };
    this.focus = this.focus.bind(this);
    this.onChange = this.onChange.bind(this);
  }

  /**
   * Called to initialize the datetimepicker
   */
  componentDidMount() {
    const $input = $(this.refs.dateTextField);
    $input
      .datetimepicker({
        defaultDate: this.value(),
        format: "L",
        icons: {
          clear: "fa fa-trash",
          close: "fa fa-times",
          date: "fa fa-calendar",
          down: "fa fa-chevron-down",
          next: "fa fa-chevron-right",
          previous: "fa fa-chevron-left",
          time: "fa fa-clock-o",
          today: "fa fa-crosshairs",
          up: "fa fa-chevron-up"
        },
        keepInvalid: true,
        locale: moment.locale(),
        useCurrent: false
      })
      .on("dp.change", this.onChange);

    // Update the min/max separately to avoid the error preventing initialization
    try {
      $(this.refs.dateTextField)
        .data("DateTimePicker")
        .options({
          maxDate: this.max(),
          minDate: this.min()
        });
    } catch (error) {
      // Catch datetimepicker error when the defaultDate is outside of the min/max range
    }
  }

  /**
   * Called to update the minDate/maxDate options for the datetimepicker input.
   *
   * @param {object} prevProps The component's props before updating
   */
  componentDidUpdate(prevProps) {
    if (this.props.max === prevProps.max && this.props.min === prevProps.min) {
      return;
    }

    try {
      $(this.refs.dateTextField)
        .data("DateTimePicker")
        .options({
          maxDate: this.max(),
          minDate: this.min()
        });
    } catch (error) {
      // Catch datetimepicker error when the defaultDate is outside of the min/max range
    }
  }

  /**
   * Returns the default value based on the component's props.
   *
   * @returns {object} The date as a moment object
   */
  defaultValue() {
    let defaultValue = this.props.value || this.props.defaultValue;

    if (typeof defaultValue === "string") {
      defaultValue = moment(defaultValue, "YYYY-MM-DD");
    }

    return defaultValue;
  }

  /**
   * Set focus to the text input field.
   */
  focus() {
    this.refs.dateTextField.focus();
  }

  /**
   * Returns the maximum date based on the component's `max` prop.
   *
   * @returns {object} The maximum date as a moment object
   */
  max() {
    const { max } = this.props;

    if (max === "today") {
      return moment().endOf("day");
    }

    if (typeof max === "string") {
      return moment(max, "YYYY-MM-DD").endOf("day");
    }

    if (typeof max === "object") {
      return moment(max).endOf("day");
    }
  }

  /**
   * Returns the minimum date based on the component's `min` prop.
   *
   * @returns {object} The minimum date as a moment object
   */
  min() {
    const { min } = this.props;

    if (min === "today") {
      return moment().startOf("day");
    }

    if (typeof min === "string") {
      return moment(min, "YYYY-MM-DD").startOf("day");
    }

    if (typeof min === "object") {
      return moment(min).startOf("day");
    }
  }

  /**
   * Handles the datetimepicker change event to update the state with the selected date.
   *
   * @param {object} event The select onChange event
   */
  onChange(event) {
    const value = event.date;

    this.setState({ value }, () => {
      // Fire the onChange event after the state updates
      if (this.props.onChange != null) {
        this.props.onChange.call(null, value);
      }
    });
  }

  /**
   * Returns the selected date.
   *
   * @returns {object} The date as a moment object
   */
  value() {
    if (this.props.value != null) {
      let { value } = this.props;

      if (typeof value === "string") {
        value = moment(value, "YYYY-MM-DD");
      }

      return moment(value);
    }

    if (this.state.value != null) {
      return moment(this.state.value);
    }
  }

  /**
   * Render the picker
   *
   * @returns {object} The React element
   */
  render() {
    return (
      <div className="input-group input-group-sm">
        <input
          className="form-control"
          disabled={this.props.disabled}
          id={this.props.id}
          onChange={() => {
            // Use datetimepicker's change event, so do nothing
          }}
          readOnly={this.props.readOnly}
          ref="dateTextField"
          type="text"
          value={this.renderDisplayedValue()}
        />
        <input disabled={this.props.disabled} name={this.props.name} type="hidden" value={this.renderHiddenValue()} />
        <span className="input-group-addon" onClick={this.focus}>
          <i className="fa fa-calendar" />
        </span>
      </div>
    );
  }

  /**
   * Render the displayed value of the text input.
   *
   * @returns {string} The selected date in the format "L"
   */
  renderDisplayedValue() {
    const value = this.value();
    if (value == null) {
      return;
    }

    return value.format("L");
  }

  /**
   * Render the value of the hidden input.
   *
   * @returns {string} The selected date in the format "MM/DD/YYYY"
   */
  renderHiddenValue() {
    const value = this.value();
    if (value == null) {
      return;
    }

    return value.format("MM/DD/YYYY");
  }
}

DatePicker.propTypes = {
  defaultValue: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  disabled: PropTypes.bool,
  id: PropTypes.string,
  max: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  min: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  name: PropTypes.string,
  onChange: PropTypes.func,
  readOnly: PropTypes.bool,
  value: (props, propName) => {
    if (!props[propName] || props.onChange || props.disabled || props.readOnly) {
      return PropTypes.checkPropTypes(
        { value: PropTypes.oneOfType([PropTypes.object, PropTypes.string]) },
        props,
        "value",
        "DatePicker"
      );
    }

    return new Error(
      "You provided a `value` prop to a form field without an " +
        "`onChange` handler. This will render a read-only field. If " +
        "the field should be mutable use `defaultValue`. Otherwise, " +
        "set either `onChange` or `readOnly`."
    );
  }
};

export default DatePicker;
